import React, { useCallback, useEffect, useRef, useState } from "react";

import { IconArrowDown, IconBack, IconEdit, IconHamburger, IconPause } from "~/components/icon";
import { format } from "~/tools/date-time";

// TODO(anton): Discuss migrating ExplorationDrawer to src/components/ — deeply Redux-integrated
import { ExplorationDrawer } from "../../components/react/drawer/ExplorationDrawer";
import { useQuery } from "../../queries/react-query";
import type { BorgAgentType } from "../models/borg-types";
import { queryBorgAgents } from "../queries/borg-agents";

import { AgentModeSelector } from "./_agent-mode-selector";
import { AssistantAvatar } from "./_assistant-avatar";
import type { ExportData, FooterInputConfig } from "./_chat-drawer-content";
import { ChatDrawerContent } from "./_chat-drawer-content";
import { ContextBar, type ContextBarConfig } from "./_context-bar";
import { FollowUpInput } from "./_follow-up-input";
import { VerifyDisclaimer } from "./_verify-disclaimer";
import type { AiAssistantInput } from "./build-question";
import { setLastConversationId } from "./drawer-memory";
import { buildTranscriptLines } from "./transcript-utils";

export type AiAssistantDrawerState = AiAssistantInput;

export interface AiAssistantDrawerProps {
	when: undefined | AiAssistantDrawerState;
}

const EMPTY_FOOTER: FooterInputConfig = {
	show: false,
	disabled: false,
	placeholder: "",
	showModeSelector: true,
	showStop: false,
	stopLabel: undefined,
};

// biome-ignore lint/suspicious/noEmptyBlockStatements: noop placeholder
const EMPTY_CONTEXT_BAR: ContextBarConfig = { pins: [], onRemovePin: () => {}, onAddPin: () => {}, removedPins: [] };

const downloadTranscript = (data: ExportData) => {
	const lines = buildTranscriptLines(data);
	const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `session-${format(Date.now(), "yyyy-MM-dd")}.md`;
	a.click();
	URL.revokeObjectURL(url);
};

const useDrawerState = (when: AiAssistantInput | undefined) => {
	const [inputValue, setInputValue] = useState("");
	const [historyOpen, setHistoryOpen] = useState(false);
	const [footerConfig, setFooterConfig] = useState<FooterInputConfig>(EMPTY_FOOTER);
	const [contentResetSeed, setContentResetSeed] = useState(0);
	const [hasExportableContent, setHasExportableContent] = useState(false);
	const [selectedMode, setSelectedMode] = useState<BorgAgentType>("adaptive");
	const [contextBarConfig, setContextBarConfig] = useState<ContextBarConfig>(EMPTY_CONTEXT_BAR);
	// biome-ignore lint/suspicious/noEmptyBlockStatements: noop placeholder until child mounts
	const submitRef = useRef<(text: string) => void>(() => {});
	// biome-ignore lint/suspicious/noEmptyBlockStatements: noop placeholder until child mounts
	const stopRef = useRef<() => void>(() => {});
	const exportDataRef = useRef<ExportData | null>(null);

	// Reset on drawer close
	useEffect(() => {
		if (when == null) {
			setHistoryOpen(false);
			setInputValue("");
			setFooterConfig(EMPTY_FOOTER);
			setContextBarConfig(EMPTY_CONTEXT_BAR);
			setHasExportableContent(false);
			exportDataRef.current = null;
			setSelectedMode("adaptive");
		}
	}, [when]);

	const handleNewConversation = useCallback(() => {
		setInputValue("");
		setSelectedMode("adaptive");
	}, []);

	const resetContent = useCallback(() => {
		setLastConversationId(undefined);
		setInputValue("");
		setHasExportableContent(false);
		exportDataRef.current = null;
		setSelectedMode("adaptive");
		setContentResetSeed((value) => value + 1);
	}, []);

	return {
		inputValue,
		setInputValue,
		historyOpen,
		setHistoryOpen,
		footerConfig,
		setFooterConfig,
		contentResetSeed,
		hasExportableContent,
		setHasExportableContent,
		selectedMode,
		setSelectedMode,
		contextBarConfig,
		setContextBarConfig,
		submitRef,
		stopRef,
		exportDataRef,
		handleNewConversation,
		resetContent,
	};
};

const DrawerHeaderActions: React.FC<{
	hasExportableContent: boolean;
	exportDataRef: React.MutableRefObject<ExportData | null>;
	onOpenHistory: () => void;
	onReset: () => void;
}> = ({ hasExportableContent, exportDataRef, onOpenHistory, onReset }) => (
	<>
		<button
			aria-label="Chat history"
			className="flex size-8 cursor-pointer items-center justify-center rounded-md text-grey-500 hover:bg-grey-100 hover:text-grey-700"
			onClick={onOpenHistory}
			type="button"
		>
			<IconHamburger className="size-4" />
		</button>
		<button
			aria-label="Download transcript"
			className="flex size-8 cursor-pointer items-center justify-center rounded-md text-grey-500 hover:bg-grey-100 hover:text-grey-700 disabled:opacity-30 disabled:hover:bg-transparent"
			disabled={!hasExportableContent}
			onClick={() => exportDataRef.current && downloadTranscript(exportDataRef.current)}
			type="button"
		>
			<IconArrowDown className="size-4" />
		</button>
		<button
			aria-label="New conversation"
			className="flex size-8 cursor-pointer items-center justify-center rounded-md text-grey-500 hover:bg-grey-100 hover:text-grey-700"
			onClick={onReset}
			type="button"
		>
			<IconEdit className="size-4" />
		</button>
	</>
);

export const AiAssistantDrawer: React.FC<AiAssistantDrawerProps> = ({ when }) => {
	const state = useDrawerState(when);
	const showAgentSelector = state.footerConfig.show && state.footerConfig.showModeSelector;
	const { data: agents } = useQuery({ ...queryBorgAgents, enabled: showAgentSelector });

	return (
		<ExplorationDrawer
			className="relative flex flex-col gap-4"
			data-testid="ai-assistant-drawer"
			footer={
				state.historyOpen ? undefined : (
					<div className="flex w-full flex-col gap-2">
						{(state.contextBarConfig.pins.length > 0 || state.contextBarConfig.removedPins.length > 0) && (
							<ContextBar
								onAddPin={state.contextBarConfig.onAddPin}
								onRemovePin={state.contextBarConfig.onRemovePin}
								pins={state.contextBarConfig.pins}
								removedPins={state.contextBarConfig.removedPins}
							/>
						)}
						<div hidden={!state.footerConfig.show}>
							<FollowUpInput
								disabled={state.footerConfig.disabled}
								onChange={state.setInputValue}
								onSubmit={(text) => {
									state.setInputValue("");
									state.submitRef.current(text);
								}}
								placeholder={state.footerConfig.placeholder}
								value={state.inputValue}
								visible={state.footerConfig.show}
							/>
						</div>
						{state.footerConfig.showStop && (
							<button
								className="flex cursor-pointer items-center justify-center gap-2 self-start rounded-full border border-grey-400 bg-white px-3 py-1.5 text-grey-800 text-xs shadow-sm transition-colors hover:border-primary-500 hover:text-primary-500"
								onClick={() => state.stopRef.current()}
								type="button"
							>
								<IconPause className="size-3.5" />
								<span>{state.footerConfig.stopLabel ?? "Stop"}</span>
							</button>
						)}
						{showAgentSelector && (
							<div className={state.footerConfig.disabled ? "opacity-70" : undefined}>
								<AgentModeSelector agents={agents} onChange={state.setSelectedMode} selected={state.selectedMode} />
							</div>
						)}
						<VerifyDisclaimer />
					</div>
				)
			}
			headerActions={
				state.historyOpen ? (
					<button
						aria-label="Back to chat"
						className="flex h-8 cursor-pointer items-center justify-center gap-1 rounded-md px-2 text-grey-500 hover:bg-grey-100 hover:text-grey-700"
						onClick={() => state.setHistoryOpen(false)}
						type="button"
					>
						<IconBack className="h-4 w-4" />
						<span className="text-xs">Back</span>
					</button>
				) : (
					<DrawerHeaderActions
						exportDataRef={state.exportDataRef}
						hasExportableContent={state.hasExportableContent}
						onOpenHistory={() => state.setHistoryOpen(true)}
						onReset={state.resetContent}
					/>
				)
			}
			headerTitle={
				<div className="flex flex-1 items-center gap-3">
					<AssistantAvatar />
					<div className="flex flex-col">
						<span className="font-medium text-grey-900 text-sm">
							{state.historyOpen ? "Session History" : "Chat with Liz"}
						</span>
						{!state.historyOpen && <span className="text-graphite-500 text-xs">AI Assistant</span>}
					</div>
				</div>
			}
			layout="full"
			when={when}
		>
			{(content) => (
				<ChatDrawerContent
					content={content}
					exportDataRef={state.exportDataRef}
					historyOpen={state.historyOpen}
					key={`chat-${state.contentResetSeed}`}
					onHistoryClose={() => state.setHistoryOpen(false)}
					onNewConversation={state.handleNewConversation}
					onResend={state.setInputValue}
					selectedMode={state.selectedMode}
					setContextBarConfig={state.setContextBarConfig}
					setFooterConfig={state.setFooterConfig}
					setHasExportableContent={state.setHasExportableContent}
					stopRef={state.stopRef}
					submitRef={state.submitRef}
				/>
			)}
		</ExplorationDrawer>
	);
};
